import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    useGetUserQuery,
    useSetOnboardingStepMutation,
    useApprovePassportMutation,
    useRejectPassportMutation,
    getPassportUrl,
} from 'api/adminApi';
import { OnboardingStepsList } from 'uiComponents/OnboardingStepsList';
import { ONBOARDING_STEPS, OnboardingStepId } from 'utils/onboardingSteps';

import styles from './style.module.scss';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic']);

const PASSPORT_STATUS_LABELS: Record<string, string> = {
    pending: 'Новый клиент',
    approved: 'Проверено',
    rejected: 'Отклонено',
};

const getPassportStatusLabel = (status: 'pending' | 'approved' | 'rejected' | null): string =>
    (status && PASSPORT_STATUS_LABELS[status]) || 'Не загружен';

type PassportPreviewProps = {
    label: string;
    fileRelativePath: string | null;
    url: string;
};

const PassportPreview = ({ label, fileRelativePath, url }: PassportPreviewProps) => {
    if (!fileRelativePath) {
        return (
            <div className={styles.passportPreview}>
                <span className={styles.passportLabel}>{label}</span>

                <div className={styles.passportPlaceholder}>Не загружено</div>
            </div>
        );
    }

    const ext = fileRelativePath.split('.').pop()?.toLowerCase() ?? '';
    const isImage = IMAGE_EXTENSIONS.has(ext);

    return (
        <div className={styles.passportPreview}>
            <span className={styles.passportLabel}>{label}</span>

            <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                className={styles.passportLink}
            >
                {isImage ? (
                    <img
                        src={url}
                        alt={label}
                        className={styles.passportImage}
                    />
                ) : (
                    <iframe
                        src={url}
                        title={label}
                        className={styles.passportFrame}
                    />
                )}
            </a>

            <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                className={styles.passportOpenLink}
            >
                Открыть оригинал
            </a>
        </div>
    );
};

export const UserDetail = () => {
    const { id } = useParams<{ id: string }>();
    const { data: user, isLoading, isError } = useGetUserQuery(id ?? '', { skip: !id });
    const [setOnboardingStep, { isLoading: isUpdating }] = useSetOnboardingStepMutation();
    const [approvePassport, { isLoading: isApproving }] = useApprovePassportMutation();
    const [rejectPassport, { isLoading: isRejecting }] = useRejectPassportMutation();
    const [selectedStep, setSelectedStep] = useState<OnboardingStepId>(1);
    const [isRejectFormOpen, setIsRejectFormOpen] = useState(false);
    const [rejectionComment, setRejectionComment] = useState('');

    useEffect(() => {
        if (user) {
            setSelectedStep(user.onboardingStep);
        }
    }, [user?.onboardingStep]);

    const handleApply = async () => {
        if (!id || isUpdating) {
            return;
        }

        try {
            await setOnboardingStep({ _id: id, onboardingStep: selectedStep }).unwrap();

            toast.success('Этап обновлён');
        } catch (error) {
            toast.error('Не удалось обновить этап');
        }
    };

    const handleApprovePassport = async () => {
        if (!id || isApproving) {
            return;
        }

        try {
            await approvePassport({ _id: id }).unwrap();

            toast.success('Паспорт принят');
        } catch (error) {
            toast.error('Не удалось принять паспорт');
        }
    };

    const handleRejectPassport = async () => {
        if (!id || isRejecting || !rejectionComment.trim()) {
            return;
        }

        try {
            await rejectPassport({ _id: id, comment: rejectionComment.trim() }).unwrap();

            toast.success('Паспорт отклонён');
            setIsRejectFormOpen(false);
            setRejectionComment('');
        } catch (error) {
            toast.error('Не удалось отклонить паспорт');
        }
    };

    if (isLoading) {
        return <div className={styles.page}>Загрузка…</div>;
    }

    if (isError || !user) {
        return (
            <div className={styles.page}>
                <Link
                    to='/'
                    className={styles.backLink}
                >
                    ← Назад к списку
                </Link>

                <p>Пользователь не найден.</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Link
                to='/'
                className={styles.backLink}
            >
                ← Назад к списку
            </Link>

            <h1>{user.fullName}</h1>
            <p className={styles.email}>{user.email}</p>

            <section className={styles.section}>
                <h2>Этапы онбординга</h2>

                <OnboardingStepsList
                    currentStep={user.onboardingStep}
                    user={user}
                />
            </section>

            <section className={styles.section}>
                <h2>Паспорт</h2>

                <div className={styles.passportPreviews}>
                    <PassportPreview
                        label='Разворот'
                        fileRelativePath={user.passportMainFile}
                        url={getPassportUrl(user._id, 'main')}
                    />

                    <PassportPreview
                        label='Прописка'
                        fileRelativePath={user.passportRegistrationFile}
                        url={getPassportUrl(user._id, 'registration')}
                    />
                </div>

                {/* Note: HEIC preview depends on the viewer's browser supporting inline HEIC
                    rendering (Safari does, Chrome/Firefox generally don't) — this is a
                    browser limitation, not something this app can fix without server-side
                    transcoding, which is out of scope. */}
            </section>

            <section className={styles.section}>
                <h2>Проверка паспорта</h2>

                <div className={styles.passportStatusRow}>
                    <span className={styles.passportStatusBadge}>
                        {getPassportStatusLabel(user.passportStatus)}
                    </span>
                </div>

                <div className={styles.overrideRow}>
                    <button
                        onClick={handleApprovePassport}
                        disabled={!user.passportMainFile || isApproving}
                        className={styles.applyButton}
                    >
                        {isApproving ? 'Принимаем…' : 'Принять'}
                    </button>

                    <button
                        onClick={() => setIsRejectFormOpen(true)}
                        disabled={!user.passportMainFile}
                        className={styles.applyButton}
                    >
                        Отклонить
                    </button>
                </div>

                {isRejectFormOpen && (
                    <div className={styles.rejectForm}>
                        <textarea
                            value={rejectionComment}
                            onChange={(event) => setRejectionComment(event.target.value)}
                            placeholder='Комментарий для пользователя'
                            className={styles.rejectTextarea}
                        />

                        <button
                            onClick={handleRejectPassport}
                            disabled={!rejectionComment.trim() || isRejecting}
                            className={styles.applyButton}
                        >
                            {isRejecting ? 'Отклоняем…' : 'Подтвердить отклонение'}
                        </button>
                    </div>
                )}

                {user.passportStatus === 'rejected' && user.passportRejectionComment && (
                    <div className={styles.rejectionCommentView}>
                        <span className={styles.passportLabel}>Текущий комментарий пользователю:</span>
                        <p>{user.passportRejectionComment}</p>
                    </div>
                )}
            </section>

            <section className={styles.section}>
                <h2>Ручной откат этапа</h2>

                <div className={styles.overrideRow}>
                    <select
                        value={selectedStep}
                        onChange={(event) => setSelectedStep(Number(event.target.value) as OnboardingStepId)}
                        className={styles.select}
                    >
                        {ONBOARDING_STEPS.map((step) => (
                            <option
                                key={step.id}
                                value={step.id}
                            >
                                {step.id}. {step.title}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={handleApply}
                        disabled={isUpdating}
                        className={styles.applyButton}
                    >
                        {isUpdating ? 'Применяем…' : 'Применить'}
                    </button>
                </div>
            </section>
        </div>
    );
};
